/**
 * Every error code gozem-api-accounts-service can return, mapped to copy a person can act on.
 *
 * GPGT-41350, DoD 3: "Every error state from the catalogue renders designed copy."
 * GPGT-41350, business rule: "Never display a raw error code."
 *
 * The codes are the single source of truth in the service's `src/core/errors.js`. This file mirrors
 * that list EXHAUSTIVELY and `error-catalogue.spec.ts` asserts the two agree, so a code added to the
 * service without copy here fails the build rather than reaching a user as `otp_send_throttled`.
 *
 * `title` and `body` are English defaults. They are the i18n keys' fallback, not the final copy —
 * the wording is design's to own, and every string here is expected to be reviewed. What is NOT
 * negotiable is that each code has an entry, a next step, and the right `kind`.
 */

/** How a screen treats the error, which is not the same question as what it says. */
export type ErrorKind =
  /** Inline, next to the field. The user retries in place. */
  | 'field'
  /** A banner on the current screen. The flow survives. */
  | 'notice'
  /** The flow is over on this screen. Offer a route out; never a dead end. */
  | 'terminal';

/**
 * How the user gets out of this state. EXPLICIT, because the alternative was worse.
 *
 * An earlier version used `action: {route: '/'}` to mean "start again", and `'/'` is wired to the
 * error page — so seven codes rendered a button that navigated the user to a different error. The
 * same mistake sent `merchant_not_eligible` to `/accounts`, which is a STATE inside /authorize and
 * has never been a route.
 *
 * Restarting cannot be a route at all: it needs the application's original /authorize query string,
 * and the only copy of that is in the store. So it is its own mode, handled by the page.
 */
export type Recovery =
  /** Navigate to `action.route`. A real, different page. */
  | 'route'
  /**
   * Restart the flow in place: back to the identifier screen, same application, fresh PKCE.
   * `SignInStore.restart()` keeps the validated authorize request, so this genuinely works.
   */
  | 'restart'
  /**
   * Nothing we can offer. Reserved for a broken sign-in LINK: there is nowhere to send someone
   * whose `redirect_uri` we will not navigate to and whose `app_code` we do not recognise.
   */
  | 'none';

export interface ErrorCopy {
  readonly kind: ErrorKind;
  readonly title: string;
  readonly body: string;
  readonly recovery: Recovery;
  /** Set if and only if `recovery` is `'route'`. */
  readonly action: { readonly label: string; readonly route: string } | null;
}

/**
 * NOTE ON TWO PAIRS THAT MUST NOT BE COLLAPSED.
 *
 * `otp_expired` vs `otp_attempts_exhausted` — the first is "the code aged out, get another"; the
 * second is "stop, this challenge is over". Same screen, different exits. The service keeps them
 * distinct as a stated invariant, and merging them here would waste that.
 *
 * `no_access_to_application` vs `no_merchant_account` — the first is "you exist, this app is not
 * yours" and should point at the apps that are; the second is "there is no merchant account behind
 * you" and should route to support. Also a stated service invariant.
 */
export const ERROR_COPY: Readonly<Record<string, ErrorCopy>> = Object.freeze({
  // ── the request itself ──
  invalid_request: {
    kind: 'terminal',
    title: 'Something went wrong with this link',
    body: 'The sign-in link is incomplete. Open the application again to start over.',
    recovery: 'none',
    action: null,
  },
  unknown_application: {
    kind: 'terminal',
    title: 'Unrecognised application',
    body: 'This sign-in link points at an application we do not know. Open the application directly.',
    recovery: 'none',
    action: null,
  },
  invalid_redirect_uri: {
    kind: 'terminal',
    // Deliberately unhelpful about the URI: we never echo a foreign redirect_uri back, and we never
    // navigate to it — not even to show this error.
    title: 'Unrecognised application',
    body: 'This sign-in link points somewhere we cannot return you to. Open the application directly.',
    recovery: 'none',
    action: null,
  },
  application_disabled: {
    kind: 'terminal',
    title: 'This application is unavailable',
    body: 'It has been turned off. Your account is unaffected.',
    recovery: 'route',
    action: { label: 'See your applications', route: '/applications' },
  },

  // ── the password step ──
  invalid_credentials: {
    kind: 'field',
    title: 'Incorrect password',
    body: 'Check your password and try again.',
    recovery: 'route',
    action: { label: 'Forgot password?', route: '/forgot-password' },
  },
  account_locked: {
    kind: 'terminal',
    title: 'Too many attempts',
    // The remaining time is substituted by the screen from `retry_after_seconds`. Stating it is a
    // DoD item: "Lockout copy states the remaining time explicitly, not 'try again later'."
    body: 'For your security this account is locked for {duration}. You can reset your password now instead.',
    recovery: 'route',
    action: { label: 'Reset password', route: '/forgot-password' },
  },
  account_disabled: {
    kind: 'terminal',
    title: 'This account is closed',
    body: 'Contact support if you think this is a mistake.',
    recovery: 'route',
    action: { label: 'Contact support', route: '/support' },
  },
  must_change_password: {
    kind: 'notice',
    title: 'You need a new password',
    body: 'Your password must be changed before you can continue. We will email you a link to set a new one.',
    /**
     * ROUTES TO THE RESET FLOW, and that is the designed remedy rather than a fallback.
     *
     * The 409 from `POST /v1/sso/password` carries a `change_token`, but no endpoint in the
     * service consumes one — `CHANGE_TOKEN` is issued in exactly one place and read nowhere. The
     * authenticated `POST /v1/sso/password/change` cannot serve this case either: it requires
     * `current_password` AND a session, and the 409 stops before a session exists.
     *
     * What the service DOES support is the reset: `requestPasswordReset` deliberately bypasses
     * `authenticationGate` because that gate "also refuses `must_change_password`, and refusing a
     * RESET on the grounds that the password must be changed would leave that user with no way
     * through at all -- the reset is their remedy". So that is where this sends them.
     */
    recovery: 'route',
    action: { label: 'Reset my password', route: '/forgot-password' },
  },

  // ── the OTP step ──
  otp_invalid: {
    kind: 'field',
    title: 'That code is not right',
    body: 'Check the code and try again.',
    recovery: 'restart',
    action: null,
  },
  otp_expired: {
    kind: 'notice',
    title: 'That code has expired',
    body: 'Codes are valid for a few minutes. Send a new one to continue.',
    recovery: 'restart',
    action: null,
  },
  otp_attempts_exhausted: {
    kind: 'terminal',
    title: 'Too many incorrect codes',
    body: 'For your security this sign-in has been stopped. Start again to get a new code.',
    recovery: 'restart',
    action: null,
  },
  otp_send_throttled: {
    kind: 'notice',
    title: 'Wait a moment before resending',
    body: 'You can request another code shortly.',
    recovery: 'restart',
    action: null,
  },
  /**
   * The service could not hand the code to the notification provider (GPGT-41335).
   *
   * DISTINCT FROM `otp_send_throttled`, and the distinction is the whole point of adding this. That
   * one means "you asked too often, wait"; this one means "we tried and could not". Telling someone
   * to wait when nothing is going to arrive however long they wait is the worse of the two lies.
   *
   * `notice` and `restart`, not `terminal`: the service supersedes the challenge before answering,
   * so a retry issues a genuinely fresh code rather than colliding with a pending one. Nothing
   * about the account is wrong, so the copy does not imply it is.
   */
  otp_send_failed: {
    kind: 'notice',
    title: 'We could not send your code',
    body: 'Something went wrong on our side. Try again in a moment.',
    recovery: 'restart',
    action: null,
  },

  // ── eligibility ──
  no_access_to_application: {
    kind: 'terminal',
    title: 'No access to this application',
    // UC-04. Offering the reachable applications is what stops this being a dead end.
    body: 'Your account is fine — it just does not have access here. These are the applications you can open.',
    recovery: 'route',
    action: { label: 'See your applications', route: '/applications' },
  },
  no_merchant_account: {
    kind: 'terminal',
    title: 'No merchant account linked',
    // UC-05. Genuinely different from the above, and routed differently on purpose.
    body: 'We could not find a merchant account for you. Support can link one.',
    recovery: 'route',
    action: { label: 'Contact support', route: '/support' },
  },
  merchant_not_eligible: {
    kind: 'terminal',
    title: 'This account cannot be used here',
    body: 'The merchant account you chose is not set up for this application.',
    recovery: 'restart',
    action: null,
  },

  // ── the code exchange (the application's problem, shown here only if we never redirect) ──
  code_expired: {
    kind: 'terminal',
    title: 'Sign-in took too long',
    body: 'Start again and you will be signed straight in.',
    recovery: 'restart',
    action: null,
  },
  code_already_used: {
    kind: 'terminal',
    title: 'This sign-in link was already used',
    body: 'Start again to get a fresh one.',
    recovery: 'restart',
    action: null,
  },
  pkce_verification_failed: {
    kind: 'terminal',
    title: 'Sign-in could not be completed securely',
    body: 'Start again from the application. If this keeps happening, contact support.',
    recovery: 'restart',
    action: null,
  },
  invalid_grant: {
    kind: 'terminal',
    title: 'Sign-in expired',
    body: 'Start again to continue.',
    recovery: 'restart',
    action: null,
  },
  token_reuse_detected: {
    kind: 'terminal',
    title: 'You have been signed out for your security',
    body: 'We detected a problem with this session and ended it. Sign in again.',
    recovery: 'restart',
    action: null,
  },

  // ── setting a password ──
  password_policy_violation: {
    kind: 'field',
    title: 'Choose a stronger password',
    body: 'This password does not meet the requirements listed above.',
    recovery: 'restart',
    action: null,
  },
  password_reused: {
    kind: 'field',
    title: 'You have used this password before',
    body: 'Choose one you have not used recently.',
    recovery: 'restart',
    action: null,
  },

  // ── reset links ──
  invalid_token: {
    kind: 'terminal',
    title: 'This reset link is not valid',
    body: 'Request a new one to set your password.',
    recovery: 'route',
    action: { label: 'Request a new link', route: '/forgot-password' },
  },
  token_expired: {
    kind: 'terminal',
    title: 'This reset link has expired',
    body: 'Links last a short while for your security. Request a new one.',
    recovery: 'route',
    action: { label: 'Request a new link', route: '/forgot-password' },
  },
  token_already_used: {
    kind: 'terminal',
    title: 'This reset link was already used',
    body: 'If you still need to change your password, request a new link.',
    recovery: 'route',
    action: { label: 'Request a new link', route: '/forgot-password' },
  },

  // ── everything else ──
  rate_limited: {
    kind: 'notice',
    title: 'Too many requests',
    body: 'Wait a moment and try again.',
    recovery: 'restart',
    action: null,
  },
  not_implemented: {
    kind: 'terminal',
    title: 'Not available yet',
    body: 'This part of sign-in is not switched on. Contact support if you were expecting it.',
    recovery: 'route',
    action: { label: 'Contact support', route: '/support' },
  },
});

/**
 * The copy for an unrecognised code.
 *
 * A code with no entry is a bug — the spec test catches it before release — but at RUNTIME the user
 * must still see a sentence rather than an identifier. This is the floor, not a substitute for an
 * entry: it says nothing specific because it cannot.
 */
export const UNKNOWN_ERROR_COPY: ErrorCopy = Object.freeze({
  kind: 'notice' as const,
  title: 'Something went wrong',
  body: 'Try again. If it keeps happening, contact support.',
  recovery: 'route' as const,
  action: { label: 'Contact support', route: '/support' } as const,
});

/**
 * Never returns undefined, and never returns the code.
 *
 * `Object.hasOwn` rather than `ERROR_COPY[code] ?? FALLBACK`. The `??` form looks equivalent and is
 * not: an object literal inherits from `Object.prototype`, so `ERROR_COPY['constructor']` is the
 * `Object` function — truthy, so `??` never fires — and the caller renders `[object Function]` as
 * the error title. Same for `toString`, `valueOf` and `hasOwnProperty`. Caught by the spec below,
 * which is the reason it exists.
 */
export function copyForError(code: string | null | undefined): ErrorCopy {
  if (!code) return UNKNOWN_ERROR_COPY;
  if (!Object.hasOwn(ERROR_COPY, code)) return UNKNOWN_ERROR_COPY;
  return ERROR_COPY[code] ?? UNKNOWN_ERROR_COPY;
}

/**
 * How close to the end the OTP screen starts naming the remaining attempts (GPGT-41748).
 *
 * ---------------------------------------------------------------------------------------------
 * MIRRORS THE SERVICE'S OWN RULE. `ATTEMPTS_REMAINING_THRESHOLD` in the service's
 * `src/core/lockout.js` is 2, with the reasoning "always returning the count would tell an attacker
 * exactly how many identities they can probe before tripping a lock; never returning it means a
 * legitimate user is locked out with no warning. Two is the compromise the copy is written for."
 *
 * The disclosure argument is weaker here — an OTP challenge sits behind a verified password and is
 * scoped to one identity, which is why the service returns the OTP count unconditionally — but the
 * COPY argument is the same one, and it is the reason this threshold exists rather than rendering
 * whatever arrives. "5 attempts remaining" on a screen nobody has guessed at yet is noise, and a
 * banner that is always present is a banner nobody reads by the time it matters. State the stake
 * when the stake is near.
 * ---------------------------------------------------------------------------------------------
 */
export const OTP_ATTEMPTS_NOTICE_THRESHOLD = 2;

/**
 * The counter copy, HERE rather than in the template.
 *
 * Same rule as every other string in this file: copy lives in the catalogue and a component renders
 * a signal. `{attempts}` is substituted by `formatAttemptsRemaining`, exactly as `{duration}` is by
 * `formatLockoutBody`.
 *
 * NOTE WHAT IT DOES NOT SAY. The mockup's wording is "before your account is temporarily locked",
 * and that is not what happens: the OTP attempt counter is independent of the password lockout as a
 * stated service invariant, and exhausting it sets the CHALLENGE to exhausted — which is why
 * `otp_attempts_exhausted` reads "this sign-in has been stopped. Start again to get a new code."
 * and says nothing about a lock. Promising a lockout that never arrives would be the one thing
 * worse than the constant `3` this replaces: copy that misdescribes the system.
 */
export const OTP_ATTEMPTS_NOTICE = 'You have {attempts} left before you will need a new code.';

/**
 * The counter sentence, or null when there is nothing honest to say.
 *
 * Null rather than a fallback string on all three of: no number from the server, a budget already
 * spent (`otp_attempts_exhausted` owns that screen and has its own copy), and a budget still
 * comfortably above the threshold. A caller renders nothing on null.
 */
export function formatAttemptsRemaining(
  attemptsRemaining: number | null | undefined
): string | null {
  if (typeof attemptsRemaining !== 'number' || !Number.isFinite(attemptsRemaining)) return null;
  if (attemptsRemaining <= 0) return null;
  if (attemptsRemaining > OTP_ATTEMPTS_NOTICE_THRESHOLD) return null;

  const attempts = attemptsRemaining === 1 ? '1 attempt' : `${attemptsRemaining} attempts`;

  return OTP_ATTEMPTS_NOTICE.replace('{attempts}', attempts);
}

/**
 * Substitutes `{duration}` in lockout copy.
 *
 * Rounds UP to the next minute: telling someone to wait 4 minutes when 4m30s remain sends them back
 * to a still-locked screen, which reads as the lock being broken.
 */
export function formatLockoutBody(body: string, retryAfterSeconds: number | undefined): string {
  if (retryAfterSeconds === undefined || retryAfterSeconds <= 0) {
    // No number from the server means we must not invent one; drop the clause instead.
    return body.replace(' for {duration}', '');
  }
  const minutes = Math.ceil(retryAfterSeconds / 60);
  const duration = minutes === 1 ? '1 minute' : `${minutes} minutes`;
  return body.replace('{duration}', duration);
}
